import React from 'react';
import {
  Box,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CallIcon from '@mui/icons-material/Call';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import CloseIcon from '@mui/icons-material/Close';

// Data for demonstration purposes
const PLAN_ORDER = ['360-days', '180-days', '90-days', '360-days-split'];
const PRODUCT_ORDER = [
  { name: 'Call Tracking', id: 'call-tracking', icon: <CallIcon sx={{ color: '#00C853' }} /> },
  { name: 'Business Phone', id: 'business-phone', icon: <PhoneInTalkIcon sx={{ color: '#00C853' }} /> },
  { name: 'AI-powered Speech Analytics', id: 'ai-speech-analytics', icon: <RecordVoiceOverIcon sx={{ color: '#00C853' }} /> },
];
const numericSx = { fontVariantNumeric: 'tabular-nums' };

const demoOffers = [
  {
    id: 'offer-1',
    name: 'Offer 1',
    monthlyTotal: '$72',
    products: [
      { name: 'Call Tracking', qtyLabel: '20 слотів', price: '$72' },
      { name: 'Business Phone', excluded: true },
      { name: 'AI-powered Speech Analytics', excluded: true },
    ],
    plans: [
      { id: '360-days', monthly: '$72', toPay: '$866', vat20: '$1039', savings: '$96' },
      { id: '180-days', monthly: '$82', toPay: '$489', vat20: '$587', savings: '$24' },
      { id: '90-days', monthly: '$94', toPay: '$281', vat20: '$338' },
      { id: '360-days-split', monthly: '$94', toPay: '$1125', vat20: '$1350' },
    ],
  },
  {
    id: 'offer-4',
    name: 'Offer #4',
    monthlyTotal: '$65',
    products: [
      { name: 'Call Tracking', qtyLabel: '15 слотів', price: '$65' },
      { name: 'Business Phone', qtyLabel: '62 користувачів', price: '$566' },
      { name: 'AI-powered Speech Analytics', qtyLabel: '100 хвилин', price: '$3' },
    ],
    plans: [
      { id: '360-days', monthly: '$65', toPay: '$774', vat20: '$929', savings: '$86' },
      { id: '180-days', excluded: true },
      { id: '90-days', excluded: true },
      { id: '360-days-split', excluded: true },
    ],
  },
];

const ComparisonMatrixTestPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const getPlanLabel = (planId) => {
    const labels = {
      '360-days': t('subscription.360days', '360 days'),
      '180-days': t('subscription.180days', '180 days'),
      '90-days': t('subscription.90days', '90 days'),
      '360-days-split': t('subscription.360daysSplit', '360 days (split payment)'),
    };
    const bonus = {
      '360-days': '(10% бонус)',
      '180-days': '(5% бонус)',
    };
    return (
      <Box component="span">
        {labels[planId] || planId}
        {bonus[planId] && <Typography component="span" sx={{ ml: 1, color: '#00C853', fontSize: 'inherit' }}>{bonus[planId]}</Typography>}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: { xs: 2, sm: 4 } }}>
      <Container maxWidth="lg">
        <Paper elevation={0} sx={{ bgcolor: 'white', boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)', borderRadius: '1rem', overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow>
                  {/* Feature Header */}
                  <TableCell sx={{ p: 3, width: '33%', border: 0 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {t('offers.feature', 'Feature')}
                    </Typography>
                  </TableCell>
                  {/* Offer Headers */}
                  {demoOffers.map((offer) => (
                    <TableCell 
                      key={offer.id} 
                      align="center"
                      sx={{
                        p: 3,
                        border: 0,
                        position: 'relative',
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {offer.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: '#00C853', ...numericSx }}>
                          {offer.monthlyTotal}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#00C853', fontWeight: 500, ml: 0.5 }}>
                          {t('calculator.perMonthShort', '/mo')}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Section: Products */}
                <TableRow>
                  <TableCell colSpan={demoOffers.length + 1} sx={{ p: 2, pt: 3, bgcolor: 'grey.100', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'grey.600', letterSpacing: '0.05em', border: 0 }}>
                    {t('offers.productsAndUsage', 'Продукти та використання')}
                  </TableCell>
                </TableRow>
                {PRODUCT_ORDER.map((product, rowIndex) => (
                  <TableRow key={product.id} sx={{ '&:last-child td': { border: 0 }, bgcolor: rowIndex % 2 !== 0 ? 'grey.50' : 'white' }}>
                    <TableCell sx={{ p: 2, fontWeight: 500, color: 'grey.800', border: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {product.icon}
                        {product.name}
                      </Box>
                    </TableCell>
                    {demoOffers.map((offer) => {
                      const p = (offer.products || []).find(x => x.name === product.name);
                      return (
                        <TableCell key={`${offer.id}-${product.id}`} align="center" sx={{ p: 2, border: 0 }}>
                          {!p || p.excluded ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '48px' }}>
                              <CloseIcon sx={{ color: '#EF5350', opacity: 0.5, fontSize: '1.2rem' }} />
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', ...numericSx }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                {p.price}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {p.qtyLabel}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

                {/* Section: Plans */}
                <TableRow>
                  <TableCell colSpan={demoOffers.length + 1} sx={{ p: 2, pt: 3, bgcolor: 'grey.100', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', color: 'grey.600', letterSpacing: '0.05em', border: 0 }}>
                    {t('calculator.subscriptionOptions', 'Опції підписки')}
                  </TableCell>
                </TableRow>
                {PLAN_ORDER.map((planId, planRowIndex) => (
                  <TableRow key={planId} sx={{ '&:last-child td': { border: 0 }, bgcolor: planRowIndex % 2 !== 0 ? 'grey.50' : 'white' }}>
                    <TableCell sx={{ p: 2, fontWeight: 500, color: 'grey.800', border: 0 }}>
                      {getPlanLabel(planId)}
                    </TableCell>
                    {demoOffers.map((offer) => {
                      const plan = (offer.plans || []).find(p => p.id === planId);
                      return (
                        <TableCell key={`${offer.id}-${planId}`} align="center" sx={{ p: 2, verticalAlign: 'middle', border: 0 }}>
                          {!plan || plan.excluded ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80px' }}>
                              <CloseIcon sx={{ color: '#EF5350', opacity: 0.5, fontSize: '1.2rem' }} />
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, ...numericSx }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  {plan.monthly} {t('calculator.perMonthShort', '/mo')}
                                </Typography>
                                {(planId === '360-days' || planId === '180-days') && plan.savings && (
                                  <Typography variant="caption" sx={{ color: '#00C853', fontWeight: 500 }}>
                                    ({t('subscription.save', 'економія')} {plan.savings})
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', my: 0.5 }}>
                                {plan.toPay} {t('subscription.toPayShort', 'до сплати')}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('subscription.vatIncluded', 'з ПДВ: {{amount}}', { amount: plan.vat20 })}
                              </Typography>
                            </Box>
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}

                {/* CTA Buttons Row */}
                <TableRow>
                  <TableCell sx={{ p: 2, border: 0, bgcolor: 'grey.100' }}></TableCell>
                  {demoOffers.map((offer) => (
                    <TableCell key={offer.id} align="center" sx={{ p: 2, border: 0, bgcolor: 'grey.100' }}>
                      <Button 
                        variant="contained"
                        fullWidth
                        sx={{
                          bgcolor: '#00C853',
                          color: 'white',
                          fontWeight: 700,
                          py: 1.5,
                          boxShadow: '0 4px 14px 0 rgba(0, 200, 83, 0.39)',
                          '&:hover': {
                            bgcolor: '#00B04F',
                          }
                        }}
                      >
                        {t('offers.details', 'Детальніше')}
                      </Button>
                    </TableCell>
                  ))}
                </TableRow>

              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>
    </Box>
  );
};

export default ComparisonMatrixTestPage;
